/**
 * UsuariosPage Component
 * 
 * Página de listagem e gerenciamento de usuários.
 * Permite visualizar, criar, editar e excluir usuários do sistema.
 * 
 * Funcionalidades:
 * - Listagem de usuários com paginação
 * - Filtros de busca
 * - Modal de criação/edição
 * - Atribuição de grupos de acesso
 * - Alteração de senha
 * - Exclusão com confirmação
 */

import React, { useState, useEffect, useCallback } from 'react'
import { Edit, Link2, Trash2, Plus, Search, User, Loader2 } from 'lucide-react'
import DataTable from '../../../../components/ERP/DataTable/DataTable'
import Modal from '../../../../components/ERP/Modal/Modal'
import FormField from '../../../../components/ERP/Form/FormField'
import { userService, accessGroupService, financialAccountService } from '../../../../services/erpApi'
import { useToast } from '../../../../hooks/useToast'
import Toast from '../../../../components/Toast/Toast'
import './UsuariosPage.css'

interface Usuario {
  id: number
  name: string
  email: string
  phone?: string
  address?: string
  cpf_cnpj?: string
  role: string
  access_group_id?: number
  access_group_name?: string
  active: boolean
  financial_account_id?: number
  financial_account_code?: string
  financial_account_name?: string
  is_seller: boolean
}

interface AccessGroup {
  id: number
  name: string
}

interface FinancialAccount {
  id: number
  code: string
  name: string
}

interface FormData {
  name: string
  email: string
  password: string
  confirmPassword: string
  phone: string
  address: string
  cpfCnpj: string
  role: string
  accessGroupId: string
  active: boolean
  financialAccountId: string
  isSeller: boolean
}

interface Pagination {
  page: number
  limit: number
  total: number
  totalPages: number
}

const UsuariosPage: React.FC = () => {
  const [usuarios, setUsuarios] = useState<Usuario[]>([])
  const [accessGroups, setAccessGroups] = useState<AccessGroup[]>([])
  const [financialAccounts, setFinancialAccounts] = useState<FinancialAccount[]>([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingUsuario, setEditingUsuario] = useState<Usuario | null>(null)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
    cpfCnpj: '',
    role: 'user',
    accessGroupId: '',
    active: true,
    financialAccountId: '',
    isSeller: false
  })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [searchTerm, setSearchTerm] = useState('')
  const [saving, setSaving] = useState(false)
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  })
  const { toast, showToast, hideToast } = useToast()

  const loadUsuarios = useCallback(async () => {
    setLoading(true)
    try {
      const response = await userService.getAll({ 
        search: searchTerm,
        page: pagination.page,
        limit: pagination.limit
      })
      if (response.success) {
        setUsuarios(response.data || [])
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            limit: response.pagination.limit,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages
          })
        }
      }
    } catch (error) {
      console.error('Erro ao carregar usuários:', error)
    } finally {
      setLoading(false)
    }
  }, [searchTerm, pagination.page, pagination.limit])

  const loadAccessGroups = useCallback(async () => {
    try {
      const response = await accessGroupService.getAll({ active: true })
      if (response.success) {
        setAccessGroups(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar grupos:', error)
    }
  }, [])

  const loadFinancialAccounts = useCallback(async () => {
    try {
      const response = await financialAccountService.getAll({ active: true })
      if (response.success) {
        setFinancialAccounts(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar contas financeiras:', error)
    }
  }, [])

  useEffect(() => {
    loadUsuarios()
    loadAccessGroups()
    loadFinancialAccounts()
  }, [loadAccessGroups, loadFinancialAccounts, loadUsuarios])

  const handleSubmit = async (e?: React.FormEvent) => {
    // Prevenir comportamento padrão se for um evento
    if (e && e.preventDefault) {
      e.preventDefault()
    }
    
    setErrors({})
    setSaving(true)

    // Validações
    if (!editingUsuario && !formData.password) {
      setErrors({ password: 'Senha é obrigatória' })
      setSaving(false)
      showToast('Senha é obrigatória para novos usuários', 'warning')
      return
    }

    if (formData.password && formData.password !== formData.confirmPassword) {
      setErrors({ confirmPassword: 'Senhas não coincidem' })
      setSaving(false)
      showToast('As senhas não coincidem', 'warning')
      return
    }

    if (formData.password && formData.password.length < 6) {
      setErrors({ password: 'A senha deve ter no mínimo 6 caracteres' })
      setSaving(false)
      showToast('A senha deve ter no mínimo 6 caracteres', 'warning')
      return
    }

    try {
      const dataToSend: any = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        address: formData.address || null,
        cpfCnpj: formData.cpfCnpj ? formData.cpfCnpj.replace(/\D/g, '') : null,
        accessGroupId: formData.accessGroupId || null,
        active: formData.active,
        financialAccountId: formData.financialAccountId || null,
        isSeller: formData.isSeller
      }

      if (formData.password) {
        dataToSend.password = formData.password
      }

      let response
      if (editingUsuario) {
        response = await userService.update(editingUsuario.id, dataToSend)
      } else {
        response = await userService.create(dataToSend)
      }

      if (response.success) {
        showToast(
          editingUsuario 
            ? '✓ Usuário atualizado com sucesso!' 
            : '✓ Usuário criado com sucesso!', 
          'success'
        )
        setModalOpen(false)
        resetForm()
        loadUsuarios()
      } else {
        const errorMessage = response.message || 'Erro ao salvar usuário'
        if (response.errors && Array.isArray(response.errors)) {
          const errorObj: Record<string, string> = {}
          response.errors.forEach((err: any) => {
            errorObj[err.param || err.path] = err.msg || err.message
          })
          setErrors(errorObj)
          showToast(errorMessage, 'error')
        } else {
          setErrors({ submit: errorMessage })
          showToast(errorMessage, 'error')
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar usuário:', error)
      const errorMessage = error.response?.data?.message || 'Erro ao salvar usuário'
      const errorDetails = error.response?.data?.errors
      
      if (errorDetails && Array.isArray(errorDetails)) {
        const errorObj: Record<string, string> = {}
        errorDetails.forEach((err: any) => {
          errorObj[err.param || err.path] = err.msg || err.message
        })
        setErrors(errorObj)
        showToast(errorMessage, 'error')
      } else {
        setErrors({ submit: errorMessage })
        showToast(errorMessage, 'error')
      }
    } finally {
      setSaving(false)
    }
  }

  const handleEdit = (usuario: Usuario) => {
    setEditingUsuario(usuario)
    setFormData({
      name: usuario.name || '',
      email: usuario.email || '',
      password: '',
      confirmPassword: '',
      phone: usuario.phone || '',
      address: usuario.address || '',
      cpfCnpj: usuario.cpf_cnpj || '',
      role: usuario.role || 'user',
      accessGroupId: usuario.access_group_id?.toString() || '',
      active: usuario.active !== undefined ? usuario.active : true,
      financialAccountId: usuario.financial_account_id?.toString() || '',
      isSeller: usuario.is_seller !== undefined ? usuario.is_seller : false
    })
    setModalOpen(true)
  }

  const handleDelete = async (usuario: Usuario) => {
    if (window.confirm(`Deseja realmente desativar o usuário ${usuario.name}?`)) {
      try {
        const response = await userService.delete(usuario.id)
        if (response.success) {
          loadUsuarios()
        }
      } catch (error) {
        console.error('Erro ao excluir usuário:', error)
        alert('Erro ao excluir usuário')
      }
    }
  }

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      confirmPassword: '',
      phone: '',
      address: '',
      cpfCnpj: '',
      role: 'user',
      accessGroupId: '',
      active: true,
      financialAccountId: '',
      isSeller: false
    })
    setEditingUsuario(null)
    setErrors({})
  }

  const copySignupLink = async () => {
    try {
      const response = await userService.signupLink()
      const token = response?.data?.token
      if (!token) {
        showToast('Não foi possível gerar o link de cadastro', 'error')
        return
      }
      const url = `${window.location.origin}/cadastro/${token}`
      await navigator.clipboard.writeText(url)
      showToast('Link de cadastro copiado', 'success')
    } catch (error) {
      showToast('Não foi possível copiar o link de cadastro', 'error')
    }
  }

  const columns = [
    { key: 'name', label: 'Nome', sortable: true },
    { key: 'email', label: 'E-mail', sortable: true },
    { 
      key: 'role', 
      label: 'Perfil',
      render: (value: string) => (
        <span className={`role-badge ${value === 'admin' ? 'admin' : 'user'}`}>
          {value === 'admin' ? 'Administrador' : 'Usuário'}
        </span>
      )
    },
    { 
      key: 'access_group_name', 
      label: 'Grupo de Acesso',
      render: (value: string) => value || '-'
    },
    {
      key: 'active',
      label: 'Status',
      render: (value: boolean) => (
        <span className={`status-badge ${value ? 'active' : 'inactive'}`}>
          {value ? 'Ativo' : 'Inativo'}
        </span>
      )
    }
  ]

  const actions = [
    {
      label: 'Editar',
      icon: Edit,
      variant: 'primary',
      onClick: handleEdit
    },
    {
      label: 'Excluir',
      icon: Trash2,
      variant: 'danger',
      onClick: handleDelete
    }
  ]

  return (
    <>
      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />
      <div className="usuarios-page">
        <div className="page-header">
        <div>
          <h1>
            <User size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
            Usuários
          </h1>
          <p>Gerencie usuários do sistema</p>
        </div>
        <div className="page-header-actions">
          <button type="button" className="btn-secondary" onClick={copySignupLink}>
            <Link2 size={18} />
            Copiar link de cadastro
          </button>
          <button
            className="btn-primary"
            onClick={() => {
              resetForm()
              setModalOpen(true)
            }}
          >
            <Plus size={18} />
            Novo Usuário
          </button>
        </div>
      </div>

      <div className="page-filters">
        <div className="search-input-wrapper">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Buscar usuários..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value)
              setPagination({ ...pagination, page: 1 })
            }}
            className="search-input"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={usuarios}
        loading={loading}
        actions={actions}
        emptyMessage="Nenhum usuário encontrado"
        onRowClick={undefined}
        pagination={pagination.total > 0 ? {
          from: (pagination.page - 1) * pagination.limit + 1,
          to: Math.min(pagination.page * pagination.limit, pagination.total),
          total: pagination.total,
          current: pagination.page,
          totalPages: pagination.totalPages,
          hasPrev: pagination.page > 1,
          hasNext: pagination.page < pagination.totalPages,
          onPrev: () => setPagination({ ...pagination, page: pagination.page - 1 }),
          onNext: () => setPagination({ ...pagination, page: pagination.page + 1 })
        } : null}
      />

      <Modal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false)
          resetForm()
        }}
        title={editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}
        size="medium"
        footer={
          <>
            <button
              className="btn-secondary"
              onClick={() => {
                setModalOpen(false)
                resetForm()
              }}
            >
              Cancelar
            </button>
            <button
              className="btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? (
                <>
                  <Loader2 className="spinner" size={16} style={{ marginRight: '0.5rem' }} />
                  Salvando...
                </>
              ) : (
                editingUsuario ? 'Salvar' : 'Criar'
              )}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <FormField
            label="Nome"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            error={errors.name}
            placeholder=""
            mask=""
            required
          />

          <FormField
            label="E-mail"
            name="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            error={errors.email}
            placeholder=""
            mask=""
            required
            disabled={!!editingUsuario}
          />

          {!editingUsuario && (
            <>
              <FormField
                label="Senha"
                name="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                error={errors.password}
                placeholder=""
                mask=""
                required
              />

              <FormField
                label="Confirmar Senha"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                error={errors.confirmPassword}
                placeholder=""
                mask=""
                required
              />
            </>
          )}

          {editingUsuario && (
            <>
              <FormField
                label="Nova Senha (deixe em branco para não alterar)"
                name="password"
                type="password"
                value={formData.password}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    password: e.target.value,
                    confirmPassword: e.target.value ? formData.confirmPassword : ''
                  })
                }
                error={errors.password}
                placeholder=""
                mask=""
              />
              {formData.password ? (
                <FormField
                  label="Confirmar Nova Senha"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  error={errors.confirmPassword}
                  placeholder=""
                  mask=""
                  required
                />
              ) : null}
            </>
          )}

          <FormField
            label="Telefone"
            name="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            error={errors.phone}
            placeholder=""
            mask="phone"
          />

          <FormField
            label="CPF/CNPJ"
            name="cpfCnpj"
            value={formData.cpfCnpj}
            onChange={(e) => {
              const value = e.target.value
              const cleanValue = value.replace(/\D/g, '')
              const masked = cleanValue.length <= 11
                ? cleanValue.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})$/, '$1-$2')
                : cleanValue.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
              setFormData({ ...formData, cpfCnpj: masked })
            }}
            error={errors.cpfCnpj}
            placeholder=""
            mask=""
          />

          <FormField
            label="Endereço"
            name="address"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            error={errors.address}
            placeholder=""
            mask=""
          />

          <div className="form-row">
            <FormField
              label="Grupo de Acesso"
              name="accessGroupId"
              type="select"
              value={formData.accessGroupId}
              onChange={(e) => setFormData({ ...formData, accessGroupId: e.target.value })}
              error=""
              placeholder=""
              mask=""
              options={[
                { value: '', label: 'Nenhum' },
                ...accessGroups.map(group => ({
                  value: group.id.toString(),
                  label: group.name
                }))
              ]}
            />
          </div>

          <FormField
            label="Status"
            name="active"
            type="select"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, active: e.target.value === 'true' })}
            error=""
            placeholder=""
            mask=""
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' }
            ]}
          />

          <FormField
            label="Conta para Abertura de Caixa"
            name="financialAccountId"
            type="select"
            value={formData.financialAccountId}
            onChange={(e) => setFormData({ ...formData, financialAccountId: e.target.value })}
            error=""
            placeholder=""
            mask=""
            options={[
              { value: '', label: 'Usar conta padrão (Caixainha)' },
              ...financialAccounts.map(account => ({
                value: account.id.toString(),
                label: `${account.code} - ${account.name}`
              }))
            ]}
          />

          <FormField
            label="Vendedor na Pré-venda"
            name="isSeller"
            type="select"
            value={formData.isSeller ? 'true' : 'false'}
            onChange={(e) => setFormData({ ...formData, isSeller: e.target.value === 'true' })}
            error=""
            placeholder=""
            mask=""
            options={[
              { value: 'true', label: 'Sim' },
              { value: 'false', label: 'Não' }
            ]}
          />

          {errors.submit && (
            <div className="form-error-message">{errors.submit}</div>
          )}
        </form>
      </Modal>
      </div>
    </>
  )
}

export default UsuariosPage
